<template>
    <div aria-live="assertive" class="finished-tests-new-page">
        <div class="col-xs-12">
            <div class="d-flex-row flex-justify-between">
                <p id="title" role="heading" aria-level="1" tabindex="0">
                    <i class="fa fa-history"></i> Provas realizadas
                </p>
            </div>
        </div>

        <div v-if="!loading">
            <div v-if="result && result.length > 0">
                <div class="col-xs-12 p-x-sm d-flex-row flex-wrap">
                    <div class="col-xs-12 p-x-md">
                        <div class="history-alert d-flex-row">
                            <img src="@/assets/img/alert-circle.svg" class="history-alert-icon" alt="" />
                            <div>
                                <p class="history-alert-title">Seu histórico de provas</p>
                                <p class="history-alert-text">Nesta tela você encontra todas as suas provas já encerradas: tanto as que você realizou (Presente) quanto aquelas em que esteve ausente (Ausente).</p>
                                <p class="history-alert-text">A situação de cada prova aparece no canto de cada cartão. Use a busca e os filtros para localizar uma prova ou situação específica.</p>
                            </div>
                        </div>
                    </div>
                    <div class="col-xs-12 p-x-md">
                        <div class="card p-20 filter-card m-b-24">
                            <div class="search-input-wrapper m-b-xl">
                                <i class="fa fa-search"></i>
                                <input
                                    type="text"
                                    v-model="searchQuery"
                                    placeholder="Buscar por disciplina ou avaliação..."
                                    class="search-input"
                                />
                            </div>

                            <div class="finished-filter-row">
                                <FilterMultiDropdown
                                    label="Ano"
                                    icon="fa fa-calendar-o"
                                    :options="yearOptions"
                                    v-model="selectedYears"
                                />
                                <FilterMultiDropdown
                                    label="Mês"
                                    icon="fa fa-calendar"
                                    :options="monthOptions"
                                    v-model="selectedMonths"
                                />
                                <FilterMultiDropdown
                                    label="Módulo"
                                    icon="fa fa-filter"
                                    :options="moduleOptions"
                                    v-model="selectedModules"
                                />
                                <FilterMultiDropdown
                                    label="Tipo"
                                    icon="fa fa-tag"
                                    :options="typeOptions"
                                    v-model="selectedTypes"
                                />
                            </div>

                            <div class="d-flex-row flex-justify-end m-t-md" v-if="hasActiveFilters">
                                <button
                                    class="btn btn-default"
                                    @click="clearFilters"
                                >
                                    Limpar filtros
                                </button>
                            </div>
                        </div>
                    </div>

                    <div
                        v-for="test in sortedTests"
                        :key="test.item_generated"
                        class="col-xs-12 col-md-6 col-lg-3 p-md m-b-md finished-test-card-wrapper"
                    >
                        <BaseTestCard :test="test" :module-name="test.module">
                            <template #header-right>
                                <span class="attendance-indicator" :class="test.attendance ? 'attendance--present' : 'attendance--absent'">
                                    <i :class="test.attendance ? 'fa fa-check-circle' : 'fa fa-times-circle'"></i>
                                    {{ test.attendance ? 'Presente' : 'Ausente' }}
                                </span>
                            </template>
                            <template #footer>
                                <div class="col-xs-12 p-0 m-b-md d-flex-row flex-justify-between flex-align-center">
                                    <small class="info">
                                        <i class="fa fa-calendar"></i> {{ formatDbDate(test.schedule.date_schedule) }} às {{ test.schedule.time_schedule }}
                                    </small>
                                    <span v-if="test.score_show && test.configs.enable_show_score_candidate" class="badge badge--score">
                                        {{ test.score }} / {{ test.value }}
                                    </span>
                                </div>
                                <div class="col-xs-12 p-0 d-flex-row" style="gap: 8px;">
                                    <button
                                        class="btn btn-primary flex-1"
                                        v-if="test.can_be_retried"
                                        @click="retryModalId = test.item_generated"
                                    >
                                        Nova tentativa
                                    </button>
                                    <router-link
                                        v-if="test.score_show && !test.configs.hide_see_documents"
                                        class="btn btn-default flex-1"
                                        :to="`/finished-tests/${test.item_generated}`"
                                    >
                                        Ver documentos
                                    </router-link>
                                </div>
                            </template>
                        </BaseTestCard>
                    </div>

                    <div v-if="filteredTests.length === 0" class="col-xs-12 d-flex-row flex-align-center flex-justify-center p-xl">
                        <img src="../../assets/img/search-logo.png" alt="Nenhum resultado" class="m-r-xl" />
                        <div class="d-flex-column">
                            <div class="no-results-title">Nenhum resultado encontrado</div>
                            <div>Não há registros que atendam aos filtros aplicados</div>
                        </div>
                    </div>
                </div>
            </div>

            <div v-else class="col-xs-12 d-flex-row flex-align-center flex-justify-center p-xl">
                <img src="../../assets/img/no-results-logo.png" alt="Nenhum resultado" class="m-r-xl" />
                <div class="d-flex-column">
                    <div class="no-results-title">Nenhum resultado encontrado</div>
                    <div>Não há provas realizadas para exibir.</div>
                </div>
            </div>
        </div>

        <div v-else role="presentation" class="col-xs-12 d-flex-column align-center flex-justify-center text-center h-100vh overflow-hidden">
            <i class="fa fa-5x fa-circle-o-notch fa-spin"></i>
        </div>

        <modal-dialog v-show="retryModalId" @close="closeRetryModal">
            <h3 class="m-b-md">ATENÇÃO</h3>
            <div class="col-xs-12 text-left">
                <p>Realmente deseja cancelar sua avaliação para realizar uma nova tentativa?<br/>
                Ao confirmar a ação sua avaliação será cancelada e uma nova avaliação ficará disponível.<br/>
                Nenhuma informação referente a esta primeira aplicação será guardada - questões, respostas, alternativas marcadas.</p>

                <p>Digite CANCELAR para confirmar a ação:</p>
                <div class="input-text input">
                    <input v-model="confirmationText" type="text" placeholder="Digite CANCELAR...">
                </div>

                <p>Motivo:</p>
                <div class="input-text input">
                    <textarea v-model="retryReason"></textarea>
                </div>

                <p>*Todos os campos são obrigatórios</p>

                <div class="col-xs-12 p-0 m-t-md text-right">
                    <button class="btn btn-danger m-r-sm" @click="retryModalId = null">Não</button>
                    <button class="btn btn-primary" v-bind="retryLoading || confirmationText !== 'CANCELAR' || !retryReason ? { disabled: 'disabled' } : {}" @click="retryOnlineTest()">
                        <i class="fa fa-spin fa-circle-o-notch" v-if="retryLoading"></i>
                        Sim
                    </button>
                </div>
            </div>
        </modal-dialog>

        <modal-dialog v-show="errorDescription" @close="errorDescription = null">
            <h3 class="m-b-md">Erro ao solicitar nova tentativa</h3>
            <div class="col-xs-12">
                {{ errorDescription }}
            </div>
        </modal-dialog>
    </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { getFinishedTests, postRetryOnlineTest } from '@/scripts/apiService/tests';
import { extractModule, formatDbDate } from '@/scripts/utils';
import FilterMultiDropdown from '../base/FilterMultiDropdown.vue';
import BaseTestCard from '../base/BaseTestCard.vue';
import ModalDialog from '../base/Modal';

const MONTH_NAMES = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

const router = useRouter();

const result = ref(null);
const loading = ref(false);
const searchQuery = ref('');
const activeSearchQuery = ref('');
const selectedYears = ref([]);
const selectedMonths = ref([]);
const selectedModules = ref([]);
const selectedTypes = ref([]);
const retryModalId = ref(null);
const confirmationText = ref(null);
const retryReason = ref(null);
const retryLoading = ref(false);
const errorDescription = ref(null);


const yearOptions = computed(() => {
    if (!result.value) return [];
    const years = [...new Set(result.value.map(t => parseInt(t.schedule.date_schedule.split('-')[0])))].sort().reverse();
    return years.map(y => ({ value: y, label: String(y) }));
});

const monthOptions = computed(() => {
    return MONTH_NAMES.map((name, i) => ({
        value: i + 1,
        label: `${String(i + 1).padStart(2, '0')} - ${name}`
    }));
});

const moduleOptions = computed(() => {
    if (!result.value) return [];
    const modules = [...new Set(result.value.map(t => t.module_name || extractModule(t.academic)).filter(Boolean))].sort();
    return modules.map(m => ({ value: m, label: m }));
});

const typeOptions = computed(() => {
    if (!result.value) return [];
    const types = [...new Set(result.value.map(t => t.schedule.type_assessment.name))].sort();
    return types.map(t => ({ value: t, label: t }));
});

watch(searchQuery, (val) => {
    if (val.length === 0 || val.length >= 3) {
        activeSearchQuery.value = val;
    }
});

const hasActiveFilters = computed(() =>
    activeSearchQuery.value || selectedYears.value.length || selectedMonths.value.length || selectedModules.value.length || selectedTypes.value.length
);

const testsWithData = computed(() => {
    if (!result.value) return [];
    return result.value.map(test => ({
        ...test,
        module: test.module_name || extractModule(test.academic)
    }));
});

const filteredTests = computed(() => {
    let tests = testsWithData.value;

    if (activeSearchQuery.value) {
        const normalize = str => str.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        const query = normalize(activeSearchQuery.value);
        tests = tests.filter(t =>
            normalize(t.academic).includes(query) ||
            normalize(t.schedule.type_assessment.name).includes(query)
        );
    }

    if (selectedYears.value.length) {
        tests = tests.filter(t => {
            const year = parseInt(t.schedule.date_schedule.split('-')[0]);
            return selectedYears.value.includes(year);
        });
    }

    if (selectedMonths.value.length) {
        tests = tests.filter(t => {
            const month = parseInt(t.schedule.date_schedule.split('-')[1]);
            return selectedMonths.value.includes(month);
        });
    }

    if (selectedModules.value.length) {
        tests = tests.filter(t => selectedModules.value.includes(t.module));
    }

    if (selectedTypes.value.length) {
        tests = tests.filter(t => selectedTypes.value.includes(t.schedule.type_assessment.name));
    }

    return tests;
});

const sortedTests = computed(() => {
    return [...filteredTests.value].sort((a, b) => {
        const moduleCompare = (a.module || '').localeCompare(b.module || '');
        if (moduleCompare !== 0) return moduleCompare;

        const dateCompare = (a.schedule?.date_schedule || '').localeCompare(b.schedule?.date_schedule || '');
        if (dateCompare !== 0) return dateCompare;

        const timeCompare = (a.schedule?.time_schedule || '').localeCompare(b.schedule?.time_schedule || '');
        if (timeCompare !== 0) return timeCompare;

        return (a.schedule?.type_assessment?.name || '').localeCompare(b.schedule?.type_assessment?.name || '');
    });
});

function clearFilters() {
    searchQuery.value = '';
    activeSearchQuery.value = '';
    selectedYears.value = [];
    selectedMonths.value = [];
    selectedModules.value = [];
    selectedTypes.value = [];
}

function closeRetryModal() {
    retryModalId.value = null;
    confirmationText.value = null;
    retryReason.value = null;
}

async function retryOnlineTest() {
    retryLoading.value = true;
    try {
        const response = await postRetryOnlineTest(retryModalId.value, {
            use_new_layout: true,
            reason: retryReason.value
        });
        router.push(`/online-tests/${response.data.item_id}/instructions`);
    } catch (error) {
        errorDescription.value = error.response?.data?.error || 'Erro inesperado. Tente novamente ou entre em contato com o suporte.';
        retryLoading.value = false;
    }
}

onMounted(() => {
    loading.value = true;
    getFinishedTests()
        .then((response) => {
            result.value = response.data;
        })
        .catch(() => {})
        .finally(() => {
            loading.value = false;
        });
    document.getElementById('title')?.focus();
});
</script>
